/**
 * Role / permission helpers — BSVAC role hierarchy (Feb 2026 refresh).
 *
 *   volunteer   → baseline: dashboard, inbox, own schedule, my tours,
 *                 on-now, trade board, supply request submission.
 *   supervisor  → + full schedule edit, Supervisor Hub, Schedule Templates,
 *                 Hours Reports, Audit Trail, drop / trade approvals.
 *   admin       → paperwork/compliance review + user management +
 *                 supply-request approval. Admin is a **narrow** office role;
 *                 it does NOT grant schedule or supervisor-hub access.
 *   staff_chief → everything except CO-only carve-outs (Validation Tool).
 *   commanding_officer → complete, unrestricted access.
 *   super_admin → complete, unrestricted access (aliased to CO).
 *
 * Users also have `additional_roles: string[]` for stacking. A Supervisor
 * with `staff_chief` in `additional_roles` gains full Staff Chief access
 * without losing their supervisor primary label.
 */

const TOP_TIER = new Set(["super_admin", "commanding_officer", "staff_chief"]);

const SUPERVISOR_CERTS = new Set(["supervisor"]);
const LOGISTICS_CERTS = new Set(["logistics", "logstics", "logisitics", "logistic"]);
const certsLower = (user) => (user?.certifications || []).map((c) => String(c).trim().toLowerCase());

/** Every role a member holds, combined (mirrors backend `effective_roles`):
 *  primary + additional roles + Supervisor / Logistics certifications + Instructor. */
export function userRoles(user) {
  if (!user) return [];
  const extra = Array.isArray(user.additional_roles) ? user.additional_roles : [];
  const owned = new Set([user.role, ...extra].filter(Boolean));
  const certs = certsLower(user);
  if (certs.some((c) => SUPERVISOR_CERTS.has(c))) owned.add("supervisor");
  if (certs.some((c) => LOGISTICS_CERTS.has(c))) owned.add("logistics");
  if ((user.instructor_classes || []).length > 0) owned.add("instructor");
  return [...owned];
}

/** True if the member is an authorized instructor (optionally for one class). */
export function isInstructor(user, classId) {
  const classes = user?.instructor_classes || [];
  return classId ? classes.includes(classId) : classes.length > 0;
}

export function hasRole(user, ...roles) {
  const owned = new Set(userRoles(user));
  return roles.some((r) => owned.has(r));
}

/** True if the user has any of the top-tier "no-restrictions" roles. */
export function isTopTier(user) {
  if (!user) return false;
  if (user.is_original_admin === true) return true;
  const owned = new Set(userRoles(user));
  for (const r of owned) if (TOP_TIER.has(r)) return true;
  return false;
}

/** Supervisor-tier access — schedule edit, Supervisor Hub, templates,
 *  Hours Reports, audit trail, drop / trade approvals. Excludes `admin`. */
export function isSupervisorOrAbove(user) {
  return isTopTier(user) || hasRole(user, "supervisor");
}

/** Admin-tier access — paperwork & compliance, user management, supply
 *  approval, settings. Admin is a narrow office role; Staff Chief / CO /
 *  Super Admin also pass. Supervisor does NOT. */
export function isAdminOrAbove(user) {
  return isTopTier(user) || hasRole(user, "admin");
}

/** CO-only gates (Validation Tool). Super Admin has identical "no
 *  restrictions" access so is included here. Staff Chief is NOT. */
export function isCommandingOfficer(user) {
  return hasRole(user, "commanding_officer", "super_admin");
}

/** Logistics access — strictly scoped per iter33:
 *  - users with the "Logistics" authorized certification
 *  - Staff Chief
 *  - Commanding Officer
 *  - Super Admin
 *  No one else (not Admin, not Supervisor, not seeded Original Admin
 *  unless they also carry the cert). */
export function hasLogisticsAccess(user) {
  if (!user) return false;
  return hasRole(user, "staff_chief", "commanding_officer", "super_admin", "logistics");
}
